'use client';

import { useEffect, useRef, useState } from 'react';
import { signIn, getSession } from 'next-auth/react';
import { useRouter, useSearchParams } from 'next/navigation';
import { Loader2, AlertTriangle } from 'lucide-react';
import { isVercelPreviewDeployment } from '@/lib/config/environment';
import {
  ACCOUNT_NOT_PROVISIONED_ERROR,
  SESSION_INVALID_ERROR
} from '@/lib/auth/federated-logout';

/**
 * Login page that automatically redirects to Keycloak
 * No local login form - goes straight to your Keycloak portal
 *
 * Uses getSession() instead of useSession() to force a fresh
 * /api/auth/session call on mount. The SessionProvider caches
 * session state across client-side navigations, so when middleware
 * redirects here after cookies are cleared, useSession() can return
 * stale data and never re-fetch until window refocus.
 *
 * SSO routing is handled inside Keycloak (native Organizations email-domain
 * routing): an SSO-domain user is routed to their firm's Microsoft IdP by
 * Keycloak's identity-first login, so the frontend always starts the plain
 * `signIn('keycloak')` flow and needs no per-IdP front-door.
 */
export default function LoginPage() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const callbackUrl = searchParams.get('callbackUrl') || '/';
  const error = searchParams.get('error');
  const signInTriggeredRef = useRef(false);
  const [sessionChecked, setSessionChecked] = useState(false);

  // Fresh session check on mount — bypasses SessionProvider cache
  useEffect(() => {
    let cancelled = false;

    async function checkSession() {
      const session = await getSession();

      if (cancelled) return;
      setSessionChecked(true);

      // Valid, healthy session → redirect to destination. Checked before
      // the error paths so a stale error param lingering in the URL can't
      // strand an already-authenticated user on the login screen.
      if (session && !session.error) {
        router.push(callbackUrl);
        return;
      }

      // Loop-breaker (LF-4567): error=OAuthSignin means a prior
      // signIn('keycloak') bounced straight back — the OAuth provider
      // (Keycloak) is unreachable, e.g. mid-deploy. A failed signIn does
      // NOT clear a stale errored JWT, so without this guard the
      // session.error branch below would re-trigger signIn on every full
      // page reload, producing the infinite /login?error=OAuthSignin loop
      // this ticket reports. OAuthSignin is non-retryable, so stop here and
      // render the error card; "Try Again" lets the user re-attempt once
      // Keycloak is back.
      if (error === 'OAuthSignin') {
        console.error('[Login] OAuth sign-in failed: provider unreachable');
        return;
      }

      // Session with error (e.g. RefreshAccessTokenError) → re-authenticate
      if (session?.error) {
        console.error('[Login] Session has error:', session.error);
        if (!signInTriggeredRef.current) {
          signInTriggeredRef.current = true;
          signIn('keycloak', { callbackUrl });
        }
        return;
      }

      // No session → handle error param or redirect to Keycloak
      if (error) {
        console.error('[Login] Auth error detected:', error);

        // Auto-retry recoverable errors:
        // - SessionExpired: token refresh failed, need fresh login
        // - OAuthCallback: stale PKCE/state cookies from previous session
        //   interfered with the callback — a fresh flow clears them
        if (error === 'SessionExpired' || error === 'OAuthCallback') {
          if (!signInTriggeredRef.current) {
            signInTriggeredRef.current = true;
            signIn('keycloak', { callbackUrl });
          }
          return;
        }

        // Other errors — don't auto-redirect (prevents loop)
        return;
      }

      // Unauthenticated — redirect to Keycloak
      if (!signInTriggeredRef.current) {
        signInTriggeredRef.current = true;

        if (isVercelPreviewDeployment()) {
          const params = new URLSearchParams({ callbackUrl });
          // eslint-disable-next-line @next/next/no-location-assign-relative-destination -- the destination is /api/auth/preview-signin, a route handler rather than a Next.js page, so `useRouter().push()` cannot reach it and the rule is simply wrong here.
          window.location.href = `/api/auth/preview-signin?${params}`;
          return;
        }

        signIn('keycloak', { callbackUrl });
      }
    }

    checkSession();

    return () => {
      cancelled = true;
    };
  }, [callbackUrl, error, router]);

  // Show error only for non-recoverable errors. SessionExpired and
  // OAuthCallback are auto-retried in the effect above, so showing the
  // error card would flash misleading UI during the silent re-signIn.
  if (
    sessionChecked &&
    error &&
    error !== 'SessionExpired' &&
    error !== 'OAuthCallback'
  ) {
    // SessionInvalid (LF-4870): the user was force-logged-out of an
    // unrecoverable session. AccountNotProvisioned (LF-5950) is its narrower
    // sibling — the identity authenticated at Keycloak but has no law-firm user
    // behind it, so "sign in again" is the wrong advice: it would land them
    // right back here. Both render a MANUAL sign-in button — never auto-signIn,
    // so re-authenticating a still-broken account can't silently re-loop. Any
    // other error keeps the existing provider-unreachable "Unable to Sign In"
    // card.
    const isAccountNotProvisioned = error === ACCOUNT_NOT_PROVISIONED_ERROR;
    const isSessionInvalid = error === SESSION_INVALID_ERROR;
    const isManualReauth = isSessionInvalid || isAccountNotProvisioned;

    const title = isAccountNotProvisioned
      ? 'Account not set up'
      : isSessionInvalid
        ? 'Signed out'
        : 'Unable to Sign In';

    const description = isAccountNotProvisioned
      ? "You signed in successfully, but this account isn't set up for this firm yet. Ask your firm's administrator to invite you, or contact support if you think this is a mistake."
      : isSessionInvalid
        ? "Your session couldn't be validated, so we've signed you out. Please sign in again, or contact support if this keeps happening."
        : "We're having trouble connecting to the authentication service. Please try again, or contact support if the problem persists.";

    const buttonLabel = isAccountNotProvisioned
      ? 'Back to sign in'
      : isSessionInvalid
        ? 'Sign in'
        : 'Try Again';

    return (
      <div className="bg-surface-subtle flex min-h-screen items-center justify-center">
        <div className="bg-card max-w-md rounded-lg p-8 text-center shadow-md">
          <div className="mb-4 flex justify-center">
            <div className="bg-error-subtle flex h-12 w-12 items-center justify-center rounded-full">
              <AlertTriangle className="text-error-subtle-foreground h-6 w-6" />
            </div>
          </div>
          <h1 className="text-foreground mb-2 text-xl font-semibold">
            {title}
          </h1>
          <p className="text-foreground-secondary mb-6 text-sm">
            {description}
          </p>
          <button
            onClick={() => {
              if (isManualReauth) {
                // Manual, explicit re-auth — Keycloak now hosts a fresh login
                // (its SSO cookie was cleared by the end-session redirect), so
                // the user can sign in as a different account.
                signIn('keycloak', { callbackUrl: '/' });
                return;
              }
              // Preserve the original destination so a successful retry
              // (once Keycloak recovers) lands the user where they were
              // headed, not on the default '/' fallback.
              router.push(
                `/login?callbackUrl=${encodeURIComponent(callbackUrl)}`
              );
            }}
            className="bg-foreground text-background hover:bg-foreground/90 focus:ring-foreground w-full rounded-md px-4 py-2 text-sm font-medium transition-colors focus:ring-2 focus:ring-offset-2 focus:outline-none"
          >
            {buttonLabel}
          </button>
        </div>
      </div>
    );
  }

  // Default state: signing the user in. Covers both first-time visits
  // (silent redirect to Keycloak) and the auto-retry path after recoverable
  // errors. The 300ms delay keeps fast paths visually blank.
  return (
    <div className="bg-card flex min-h-screen items-center justify-center">
      <div className="animate-in fade-in text-foreground-secondary flex items-center gap-3 text-sm [animation-delay:300ms] [animation-duration:200ms] [animation-fill-mode:both]">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>Signing you in…</span>
      </div>
    </div>
  );
}
